import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SlidersHorizontal, Heart } from 'lucide-react';
import axios from 'axios';

import { usePageTitle } from '../../hooks/usePageTitle';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePrivacy } from '../../contexts/PrivacyContext';

import Sidebar from '../Sidebar';
import Header from '../Header';
import DarkTooltip from '../ui/DarkTooltip';
import PremiumFeatureBlock from '../ui/PremiumFeatureBlock';

import NicheFinderHeader from './NicheFinderHeader';
import NicheFinderTabs, { TAB_DEFS } from './NicheFinderTabs';
import NicheFinderFilters from './NicheFinderFilters';
import NicheFinderFiltersModal from './NicheFinderFiltersModal';
import NicheFinderGrid from './NicheFinderGrid';
import NicheFinderUploadModal from './NicheFinderUploadModal';
import NicheFinderAddChannelModal from './NicheFinderAddChannelModal';
import CategoryGroupedViewCompact from './CategoryGroupedViewCompact';
import NicheFinderFavoritesView from './NicheFinderFavoritesView';
import NicheFinderTeaser from './NicheFinderTeaser';

import { useNicheFinderStats } from './hooks/useNicheFinderStats';
import { useNicheFinderChannels } from './hooks/useNicheFinderChannels';
import { useNicheFinderFilters } from './hooks/useNicheFinderFilters';
import { useFavorites } from './hooks/useFavorites';
import {
  filterChannelsBySearch,
  getCategoryName,
  getLanguageName,
  getVisitedChannels,
  markChannelAsVisited,
} from './utils';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

/**
 * NicheFinderPage — orquestrador da pagina (Sprint 3).
 *
 * Substitui NichosViraisV2Page. Usa o design system real (CSS vars,
 * PageHeader, .dp-card-interactive) e i18n PT/EN/ES. Reusa AddChannelModal
 * de NichosViraisModals.jsx; upload tem componente proprio simples.
 */
const NicheFinderPage = ({ onLogout, isSuperAdmin }) => {
  const { t } = useTranslation();
  usePageTitle(t('viralNiches.title'));
  useTheme(); // subscribe pra reatividade de CSS vars
  const { sidebarWidth, isMobile } = useSidebar();
  const { isPrivate, togglePrivacy } = usePrivacy();

  // ===== Access check (gate de plano / super admin) =====
  const [accessCheck, setAccessCheck] = useState({
    loading: true, hasAccess: false, message: '',
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/auth/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (data.is_super_admin) {
          setAccessCheck({ loading: false, hasAccess: true, message: '' });
          return;
        }
        setAccessCheck({
          loading: false,
          hasAccess: !!data.nichos_virais_acesso,
          message: data.nichos_virais_mensagem || t('viralNiches.resource_blocked_plan'),
        });
      } catch (err) {
        if (cancelled) return;
        setAccessCheck({
          loading: false,
          hasAccess: false,
          message: t('viralNiches.error_check_access'),
        });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [t]);

  // ===== Tab state (URL ?tab=) =====
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabId = searchParams.get('tab');
  const validTabIds = TAB_DEFS.map((tab) => tab.id);
  const [activeTabId, setActiveTabId] = useState(
    initialTabId && validTabIds.includes(initialTabId) ? initialTabId : 'exploding',
  );
  const activeTab = TAB_DEFS.find((tab) => tab.id === activeTabId) || TAB_DEFS[0];

  const handleTabChange = (tab) => {
    setActiveTabId(tab.id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab.id);
      return next;
    }, { replace: true });
  };

  // ===== Stats + Channels =====
  const enabled = accessCheck.hasAccess;
  const { stats, refetch: refetchStats } = useNicheFinderStats({ enabled });
  // ===== Filters =====
  // Filters precisa rodar ANTES de useNicheFinderChannels pra passar
  // sortBy. Ordem dos hooks importa pra React (mesma ordem em todo
  // render) — mover pra cima eh OK pq nao tem dependencia ciclica.
  const {
    filters, setFilter, setFilters, clearFilters,
    hasActiveFilters, advancedFilterCount,
    availableLanguages, availableCategories,
  } = useNicheFinderFilters({ enabled });
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);

  // Favoritos (Pacote 5) — Set de channel_ids + toggle otimista (rollback no erro).
  const { toggleFavorite, isFavorited } = useFavorites({ enabled });

  const {
    channels: rawChannels, loading, error, refetch: refetchChannels,
    loadMore, hasMore,
  } = useNicheFinderChannels({
    filter: activeTab.filter,
    sortBy: filters.sortBy,
    // Tab "Por categoria" busca seus proprios dados (CategoryGroupedViewCompact
    // via /by-category-grouped + /category-channels), entao NAO carregamos
    // rawChannels nela — economiza um fetch de ate 500 linhas.
    enabled: enabled && !!activeTab.filter && activeTabId !== 'byCategory',
    limit: 30,
    // Pacote 7.5: filtros agora vão pro backend (busca/views funcionam sobre a
    // base inteira, não só os 30 carregados). O filtro client-side em
    // filteredChannels permanece como passe secundário consistente.
    search: filters.search,
    languages: filters.languages,
    subsMin: filters.subsMin,
    subsMax: filters.subsMax,
    viewsMin: filters.viewsMin,
    viewsMax: filters.viewsMax,
    videosMin: filters.videosMin,
    videosMax: filters.videosMax,
    monetized: filters.monetized,
    addedAfter: filters.addedAfter,
  });

  // availableLanguages/availableCategories agora vêm de useNicheFinderFilters
  // (via /filter-options — todas as opções do banco, não só dos carregados).

  // Aplica filtros e ordenacao local — shape novo (Sprint 3 v4)
  const filteredChannels = useMemo(() => {
    if (!Array.isArray(rawChannels)) return [];
    let list = rawChannels;

    // [REMOVIDO] piso automático subs>=100 && uploads>=3: escondia canais virais de
    // poucos vídeos (1-2) que o BACKEND já aprova. O gate de qualidade
    // (longform_count_sample>0 + is_shorts_channel=false + last_enriched_at) e a
    // classificação por tier (views/posting_age/subs) ficam 100% no backend; o
    // frontend NÃO re-filtra por subs/uploads. Abaixo só os filtros que o USUÁRIO
    // liga pela UI (busca, idioma, categoria, views/inscritos, monetizados, ordenar).

    // Busca inteligente: title + url + description + categoria +
    // categorias secundarias + keywords + country + idioma (com traducao).
    if (filters.search) {
      list = filterChannelsBySearch(list, filters.search, {
        getLanguageNameFn: (iso) => getLanguageName(iso, t),
        getCategoryNameFn: (cat) => getCategoryName(cat, t),
      });
    }

    if (filters.languages && filters.languages.length > 0) {
      const set = new Set(filters.languages);
      list = list.filter((ch) => ch.language_detected && set.has(ch.language_detected));
    }

    if (filters.categories && filters.categories.length > 0) {
      const set = new Set(filters.categories);
      list = list.filter((ch) => {
        if (ch.primary_category && set.has(ch.primary_category)) return true;
        if (Array.isArray(ch.categories) && ch.categories.some((c) => set.has(c))) return true;
        return false;
      });
    }

    if (filters.subsMin !== null && filters.subsMin !== undefined) {
      list = list.filter((ch) => (ch.subscribers || 0) >= filters.subsMin);
    }
    if (filters.subsMax !== null && filters.subsMax !== undefined) {
      list = list.filter((ch) => (ch.subscribers || 0) <= filters.subsMax);
    }
    if (filters.viewsMin !== null && filters.viewsMin !== undefined) {
      list = list.filter((ch) => (ch.total_views || 0) >= filters.viewsMin);
    }
    if (filters.viewsMax !== null && filters.viewsMax !== undefined) {
      list = list.filter((ch) => (ch.total_views || 0) <= filters.viewsMax);
    }
    if (filters.videosMin !== null && filters.videosMin !== undefined) {
      list = list.filter((ch) => (ch.num_uploads || 0) >= filters.videosMin);
    }
    if (filters.videosMax !== null && filters.videosMax !== undefined) {
      list = list.filter((ch) => (ch.num_uploads || 0) <= filters.videosMax);
    }

    if (filters.monetized === true) {
      list = list.filter((ch) => !!ch.is_monetized);
    } else if (filters.monetized === false) {
      list = list.filter((ch) => !ch.is_monetized);
    }

    // Pacote 7.6: filtro Faceless removido (toda a base e faceless). A metrica
    // % FACELESS continua exibida no card.

    // Filtro temporal "Adicionado em" — imported_at >= cutoff
    if (filters.addedAfter && filters.addedAfter !== 'any') {
      const now = Date.now();
      const cutoffMs = {
        last_hour: 60 * 60 * 1000,
        last_24h:  24 * 60 * 60 * 1000,
        last_7d:   7 * 24 * 60 * 60 * 1000,
        last_30d:  30 * 24 * 60 * 60 * 1000,
      }[filters.addedAfter];
      if (cutoffMs) {
        const cutoff = now - cutoffMs;
        list = list.filter((ch) => {
          const t0 = ch.imported_at ? new Date(ch.imported_at).getTime() : 0;
          return t0 >= cutoff;
        });
      }
    }

    // Sort — default 'most_views' (total_views DESC). Pacote 7.5.
    const sortBy = filters.sortBy || 'most_views';
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'most_views':
          return (b.total_views || 0) - (a.total_views || 0);
        case 'most_subs':
          return (b.subscribers || 0) - (a.subscribers || 0);
        case 'highest_multi': {
          const ma = (a.subscribers || 0) > 0 ? (a.total_views || 0) / a.subscribers : 0;
          const mb = (b.subscribers || 0) > 0 ? (b.total_views || 0) / b.subscribers : 0;
          return mb - ma;
        }
        case 'most_videos':
          return (b.num_uploads || 0) - (a.num_uploads || 0);
        case 'most_recent':
        default: {
          // Default: ordena por imported_at DESC (canais adicionados mais
          // recentemente primeiro). Fallback pra last_update / last_enriched_at
          // se imported_at faltar (canais legados pre-coluna).
          const ta = new Date(a.imported_at || a.last_update || a.last_enriched_at || 0).getTime();
          const tb = new Date(b.imported_at || b.last_update || b.last_enriched_at || 0).getTime();
          return tb - ta;
        }
      }
    });

    return sorted;
  }, [rawChannels, filters, t]);

  // ===== Visited =====
  const [visitedList, setVisitedList] = useState(() => getVisitedChannels());
  const handleVisit = useCallback((channelId) => {
    const next = markChannelAsVisited(channelId);
    setVisitedList([...next]);
  }, []);

  // ===== Admin actions =====
  const handleDelete = useCallback(async (channelId) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(t('viralNiches.confirm_delete_channel'))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/nichos-virais/channels/${channelId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('viralNiches.channel_deleted_success'));
      refetchChannels();
      refetchStats();
    } catch (err) {
      toast.error(t('viralNiches.error_delete_channel'));
    }
  }, [isSuperAdmin, t, refetchChannels, refetchStats]);

  const handleReenrich = useCallback(async (channelId) => {
    if (!isSuperAdmin) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/niche-finder/channels/${channelId}/reenrich`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('viralNiches.reenrich_success'));
    } catch (err) {
      toast.error(t('viralNiches.reenrich_error'));
    }
  }, [isSuperAdmin, t]);

  // ===== Modals =====
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // BLOQUEIO PREMIUM — MESMA posição/layout das outras 3 páginas (early return):
  // Sidebar + Header + PremiumFeatureBlock (que é dono do main centralizado). NÃO
  // renderiza o NicheFinderHeader no estado bloqueado, p/ o card ficar centralizado
  // igual às demais (antes ficava no fluxo de conteúdo, abaixo do header → desalinhado).
  // Sem acesso (free/basic/lite) → MODO TEASER: 1 fileira da amostra do dia
  // por tier + upsell (vitrine de conversão). Em erro, o Teaser degrada
  // internamente para o PremiumFeatureBlock (comportamento antigo).
  if (!accessCheck.loading && !accessCheck.hasAccess) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={!!isSuperAdmin} />
        <Header />
        <NicheFinderTeaser />
      </div>
    );
  }

  // ===== Render =====
  return (
    // Wrapper padrão de página (igual às demais): garante o fundo
    // var(--dp-bg-primary) — sem ele, o fragment deixava vazar o fundo
    // default do app (cinza fora do padrão).
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />
      <main
        className="dp-main-content dp-fade-in-up"
        style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          transition: 'margin-left 0.3s ease',
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--dp-font)',
        }}
      >
        <NicheFinderHeader
          stats={stats}
          isSuperAdmin={!!isSuperAdmin}
          isPrivacyMode={isPrivate}
          onTogglePrivacy={togglePrivacy}
          onOpenUpload={() => setShowUpload(true)}
          onOpenAdd={() => setShowAdd(true)}
        />

        {showFavorites ? (
          <NicheFinderFavoritesView
            isPrivacyMode={isPrivate}
            isSuperAdmin={!!isSuperAdmin}
            visitedList={visitedList}
            onVisit={handleVisit}
            onDelete={handleDelete}
            onReenrich={handleReenrich}
            isFavorited={isFavorited}
            onToggleFavorite={toggleFavorite}
            onBack={() => setShowFavorites(false)}
          />
        ) : (
          <>
            <NicheFinderTabs
              activeTab={activeTabId}
              counts={stats}
              onTabChange={handleTabChange}
            />
            {/* Linha unica: descricao contextual (esquerda) +
                botoes Favoritos/Filtros (direita). Pacote 7.5: a barra de
                filtros e o botao de filtros avancados aparecem em TODAS as
                abas (incluindo 'byCategory' — CategoryGroupedViewCompact ja
                consome os mesmos filtros via mapFiltersToCategoryParams). */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginTop: 4,
                marginBottom: 12,
                paddingLeft: 14,
                paddingRight: 12,
                minHeight: 36,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--dp-text-tertiary)',
                  fontFamily: 'var(--dp-font)',
                  minHeight: 16,
                  transition: 'opacity 0.2s ease',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {t(activeTab.descKey)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <FavoritesButton
                  onClick={() => setShowFavorites(true)}
                  label={t('viralNiches.favorites_button')}
                />
                <FiltersIconButton
                  count={advancedFilterCount}
                  onClick={() => setShowFiltersModal(true)}
                  ariaLabel={t('viralNiches.filters_button')}
                />
              </div>
            </div>
            <NicheFinderFilters
              filters={filters}
              onFilterChange={setFilter}
              onClear={clearFilters}
              hasActiveFilters={hasActiveFilters}
              availableLanguages={availableLanguages}
              availableCategories={availableCategories}
            />
            {activeTabId === 'byCategory' ? (
              <CategoryGroupedViewCompact
                filters={filters}
                isPrivacyMode={isPrivate}
                visitedList={visitedList}
                onVisit={handleVisit}
              />
            ) : (
              <NicheFinderGrid
                channels={filteredChannels}
                loading={loading || accessCheck.loading}
                error={error}
                onRetry={refetchChannels}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearFilters}
                hasMore={hasMore}
                loadMore={loadMore}
                visitedList={visitedList}
                isSuperAdmin={!!isSuperAdmin}
                isPrivacyMode={isPrivate}
                onVisit={handleVisit}
                onDelete={handleDelete}
                onReenrich={handleReenrich}
                isFavorited={isFavorited}
                onToggleFavorite={toggleFavorite}
              />
            )}
          </>
        )}
      </main>

      {showUpload && (
        <NicheFinderUploadModal
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            refetchChannels();
            refetchStats();
          }}
        />
      )}

      {showAdd && (
        <NicheFinderAddChannelModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => {
            setShowAdd(false);
            refetchChannels();
            refetchStats();
          }}
        />
      )}

      <NicheFinderFiltersModal
        isOpen={showFiltersModal}
        currentFilters={filters}
        channelsPreviewCount={filteredChannels.length}
        onApply={(patch) => setFilters(patch)}
        onClose={() => setShowFiltersModal(false)}
      />
    </div>
  );
};

/**
 * FiltersIconButton — botao quadrado 36x36 usado dentro do flex row
 * que tambem renderiza a descricao contextual da tab. Abre o modal
 * de filtros avancados. Badge azul com contagem de filtros nao-
 * busca/sort ativos.
 */
const FiltersIconButton = ({ count, onClick, ariaLabel }) => {
  const active = count > 0;
  return (
    <DarkTooltip content={ariaLabel}>
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 36,
        height: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        background: active ? 'rgba(13,174,255,0.10)' : 'transparent',
        border: `0.5px solid ${active ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-medium)'}`,
        color: active ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <SlidersHorizontal size={16} />
      {active && (
        <span
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 999,
            background: 'var(--dp-blue-500)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {count}
        </span>
      )}
    </button>
    </DarkTooltip>
  );
};

/**
 * FavoritesButton — botão "Favoritos" ao lado do botão de filtros (Pacote 5).
 * Abre a vista dedicada de favoritos.
 */
const FavoritesButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 36,
      padding: '0 12px',
      borderRadius: 8,
      background: 'transparent',
      border: '0.5px solid var(--dp-border-medium)',
      color: 'var(--dp-text-secondary)',
      cursor: 'pointer',
      fontSize: 13,
      fontFamily: 'var(--dp-font)',
      whiteSpace: 'nowrap',
      transition: 'background 0.15s, color 0.15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
      e.currentTarget.style.color = 'var(--dp-red-500)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = 'var(--dp-text-secondary)';
    }}
  >
    <Heart size={15} />
    <span>{label}</span>
  </button>
);

export default NicheFinderPage;
